import { mui } from "@/mui";
import { UserIcon, MessageSquareIcon, Trash2Icon, PencilIcon, Zap, RefreshCw, AlertCircle, Signal, SignalLow, SignalMedium, SignalHigh, Moon, QrCodeIcon, CopyIcon, TicketIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, MailIcon, SendIcon, XIcon } from "lucide-react";
import { InviteSection } from "./invite-section";
import type { ReactNode } from "react";
import { useState, useEffect, useRef, type FormEvent } from "react";
import { useLiveQuery } from 'dexie-react-hooks';
import { useMobyDB, useMobyNode, useSession } from "./provider";
import { Link, useLocation } from "wouter";
import type { ConnectionDiagnostic } from "@/lib/connection-diagnostics";
import { createAndSignContactDetails, parseAndVerifyContactDetails } from "@/moby/repo";
import { bytesToHex } from "@noble/curves/abstract/utils";
import { EditContactDialog } from "./edit-contact-dialog";
import { getPeerAvatarColor, renderPeerAvatarEmoji, renderPeerPresenceBadge } from "@/lib/peer-profile-utils";
import { getPeerUIDescriptor } from "@/lib/peer-ui-state";
import { PeerHealthDot, usePeerLifecycle, usePeerMetrics } from "./peer-health-dot";
import { QRCodeSVG } from 'qrcode.react';
import { toast } from "sonner";
import { z } from "zod";

function getEmailGatewayUrl(): string {
  try {
    // Email gateway URL is stored with moby_pref_ prefix by email-drawer.tsx
    const stored = localStorage.getItem('moby_pref_emailGatewayUrl');
    if (stored) {
      return stored;
    }
    // Fallback: try legacy localPreferences format
    const legacyStored = localStorage.getItem('localPreferences');
    if (legacyStored) {
      const prefs = JSON.parse(legacyStored);
      return prefs.emailGatewayUrl || '';
    }
  } catch {
    // Ignore parse errors
  }
  return '';
}

interface EmailAccount {
  id: string;
  email: string;
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  authType: "password" | "oauth2";
  accessToken?: string;
}

function getEmailAccounts(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) {
      return JSON.parse(stored) as EmailAccount[];
    }
  } catch {
    // ignore
  }
  return [];
}

/**
 * PeerDiagnosticBadge — lifecycle-store-driven diagnostic indicator.
 *
 * Renders the same icon / tone / tooltip as before, but subscribes to
 * `peerStateStore` via `usePeerLifecycle` instead of polling
 * `globalThis.node.peers` + `getStats()` every 10 s. Eliminates the
 * per-badge `getStats()` lock contention that was the #3 bottleneck
 * identified in the 5/1 P2P scale audit (`docs/connection-state-machine.md`
 * — "getStats() fragmentation across components").
 *
 * Data flow:
 *   1. `usePeerLifecycle(peerId)` → event-driven snapshot (`uiStateRaw`,
 *      `modifiers`). The store's `relay` modifier already carries the
 *      direct-vs-relay classification that this badge used to re-fetch
 *      via its own `getStats()` call; the `uiStateRaw` enum resolves to
 *      `connected-relay` / `connected-direct` accordingly.
 *   2. A slow 30 s poll reads the ancillary fields that enrich the
 *      descriptor tooltip but aren't in the snapshot: `retryPhase`,
 *      `reconnectionAttempts` (for the `reconnecting` phase suffix),
 *      and `errorCount` from `ConnectionDiagnostics` (for the `failed`
 *      tooltip). These are cheap property reads — no `getStats()`,
 *      no network-thread lock.
 *   3. Descriptor recomputed locally with full context so the tooltip
 *      retains the "Reconnecting (aggressive phase, attempt 3)" and
 *      "Connection failed (5 errors)" strings.
 */
interface DiagnosticAncillary {
  retryPhase?: 'aggressive' | 'moderate' | 'background' | 'dormant';
  reconnectionAttempts?: number;
  errorCount?: number;
}

const ANCILLARY_POLL_INTERVAL_MS = 30000;

const PeerDiagnosticBadge = ({ peerId }: { peerId: string }) => {
  const { snapshot, descriptor: storeDescriptor } = usePeerLifecycle(peerId);
  const metrics = usePeerMetrics(peerId);
  const [ancillary, setAncillary] = useState<DiagnosticAncillary>({});

  // Ancillary fields (retryPhase / reconnectionAttempts / errorCount) aren't
  // part of the store snapshot because they change independently of lifecycle
  // transitions. Poll them on a slow 30 s cadence — no `getStats()`, just
  // property reads — and re-read on snapshot changes so a lifecycle transition
  // that bumps the reconnection counter refreshes the tooltip promptly.
  useEffect(() => {
    const readAncillary = (): DiagnosticAncillary => {
      if (typeof document !== 'undefined' && document.hidden) return ancillary;
      const node = (globalThis as any).node;
      const peer = node?.peers?.get?.(peerId);
      if (!peer) return {};
      let errorCount = 0;
      try {
        if (peer.diagnostics) {
          const diagnostics: ConnectionDiagnostic[] = peer.diagnostics.getDiagnostics(peerId);
          errorCount = diagnostics.filter(d => d.severity === 'error').length;
        }
      } catch {
        // Diagnostics ring may be disposed during peer teardown — fall through.
      }
      return {
        retryPhase: peer.retryPhase,
        reconnectionAttempts: peer.reconnectionAttempts,
        errorCount,
      };
    };

    setAncillary(readAncillary());
    const interval = setInterval(() => {
      setAncillary(readAncillary());
    }, ANCILLARY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // Re-run on snapshot.uiStateRaw transitions so e.g. connected → reconnecting
    // doesn't leave the tooltip stuck at the stale retryPhase for up to 30 s.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerId, snapshot?.uiStateRaw]);

  // If the store hasn't attached this peer yet (e.g. a contact row for a
  // peer that has never been connected in this session), render nothing —
  // same behavior as the old "diagnosticInfo === null" early return.
  if (!snapshot) return null;

  // Recompute descriptor with ancillary context so the tooltip retains
  // the phase/attempt/error suffixes. The store's own descriptor was built
  // with `{ localBusy }` only — RTT and healthScore are pulled from the
  // metrics worker (5/3 fix: previously the diagnostic tooltip always
  // showed "RTT N/A" because no caller plumbed the data).
  const descriptor = getPeerUIDescriptor(snapshot.uiStateRaw, {
    localBusy: snapshot.modifiers.localBusy,
    retryPhase: ancillary.retryPhase,
    reconnectionAttempts: ancillary.reconnectionAttempts,
    errorCount: ancillary.errorCount,
    rtt: metrics.rtt ?? undefined,
    jitter: metrics.jitter ?? undefined,
    healthScore: metrics.healthScore ?? undefined,
  });
  // Reference storeDescriptor defensively so a future refactor that returns
  // a richer descriptor from the store (e.g. with health-score context) can
  // drop the local recompute in one edit. Today it's identity-equal to the
  // locally recomputed descriptor for every branch except the tooltip.
  void storeDescriptor;

  // Map descriptor iconKey → Lucide component. Keeping this switch in the
  // component preserves the existing tailwind-driven color classes for
  // sharp text rendering (dots use inline hex because borders vary).
  const iconColorClass =
    descriptor.tone === 'green' ? 'text-green-500'
    : descriptor.tone === 'blue' ? 'text-blue-500'
    : descriptor.tone === 'amber' ? 'text-amber-500'
    : descriptor.tone === 'red' ? 'text-red-500'
    : descriptor.tone === 'dimmed' ? 'text-gray-400'
    : 'text-gray-500';
  const spinClass = descriptor.spin ? ' animate-spin' : '';
  const className = `${iconColorClass}${spinClass}`;

  let icon: React.ReactNode;
  switch (descriptor.iconKey) {
    case 'zap':          icon = <Zap className={className} size={14} />; break;
    case 'refresh':      icon = <RefreshCw className={className} size={14} />; break;
    case 'signal':       icon = <Signal className={className} size={14} />; break;
    case 'signalLow':    icon = <SignalLow className={className} size={14} />; break;
    case 'signalMedium': icon = <SignalMedium className={className} size={14} />; break;
    case 'signalHigh':   icon = <SignalHigh className={className} size={14} />; break;
    case 'alert':        icon = <AlertCircle className={className} size={14} />; break;
    case 'moon':         icon = <Moon className={className} size={14} />; break;
    case 'dot':          icon = <Signal className={className} size={14} />; break;
  }

  return (
    <div
      className="flex items-center gap-1"
      title={descriptor.tooltip}
      data-peer-ui-state={snapshot.uiStateRaw}
    >
      {icon}
    </div>
  );
};
export function ContactsDrawer({ children, open: controlledOpen, onOpenChange: controlledOnOpenChange }: { children: ReactNode | ((props: { unseenCount: number }) => ReactNode); open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const db = useMobyDB();
  const session = useSession();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };
  const [showQRCode, setShowQRCode] = useState(false);
  const [theirCode, setTheirCode] = useState('');
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const navigate = useLocation()[1];
  
  // Listen for external open requests (e.g. from empty chat list)
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener("open-contacts-drawer", open);
    return () => window.removeEventListener("open-contacts-drawer", open);
  }, []);
  
  // Collapsible Add Contact section state
  const [isAddContactExpanded, setIsAddContactExpanded] = useState(false);
  
  // Collapsible Pending Invites section state
  const [isPendingExpanded, setIsPendingExpanded] = useState(true);
  
  // Email invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [selectedFromAccount, setSelectedFromAccount] = useState<string>('');
  
  // Get enabled email accounts for "From" dropdown
  const emailAccounts = getEmailAccounts().filter(a => a.enabled);
  
  // Auto-select first account if none selected
  useEffect(() => {
    if (!selectedFromAccount && emailAccounts.length > 0) {
      setSelectedFromAccount(emailAccounts[0].id);
    }
  }, [emailAccounts.length, selectedFromAccount]);
  
  // =====================================================================
  // UI REACTIVITY FIX - deletion refresh trigger for contacts drawer
  // =====================================================================
  // State to force refresh of contacts list when remote deletions occur
  const [deletionRefreshTrigger, setDeletionRefreshTrigger] = useState(0);

  const contacts = useLiveQuery(async () => {
    const rawContacts = await db.contacts.toArray();
    
    // CRITICAL FIX: Filter out tombstoned contacts
    // This ensures deleted contacts don't appear in UI after deletion
    const activeContacts = [];
    for (const contact of rawContacts) {
      const isTombstoned = await db.isContactTombstoned(contact.address);
      if (!isTombstoned) {
        activeContacts.push(contact);
      } else {
        console.debug(`[ContactsDrawer] Filtered out tombstoned contact ${contact.address.slice(0, 16)}...`);
      }
    }
    
    // Process contacts to include connection health data and ensure proper methods
    return activeContacts.map(contact => {
      // Create a safe contact object with connection health data
      const contactWithHealth = {
        ...contact,
        // Include connection health metrics for presence indicators (same as chat-list-panel)
        connectionHealth: contact.connection,
        // Ensure required methods exist (prevent crashes from prototype stripping)
        getInitials() {
          return (this.nickname ?? this.card?.name ?? this.address).slice(0, 2);
        },
        getDisplayName() {
          if (this.nickname) {
            return this.nickname;
          }
          if (this.card?.name) {
            return this.card.name;
          }
          const shortAddress = this.address.substring(0, 8);
          return `User-${shortAddress}`;
        }
      };
      
      return contactWithHealth;
    });
  }, [deletionRefreshTrigger]); // Include refresh trigger to force re-query on remote deletions

  // Query pending invites for display
  const pendingInvites = useLiveQuery(async () => {
    return await db.pendingInvites
      .where('status')
      .equals('pending')
      .toArray();
  }, [deletionRefreshTrigger]);

  const unseenCount = (contacts ?? []).filter(contact => contact.custom?.unseen).length;
  const unseenAddresses = (contacts ?? []).filter(c => c.custom?.unseen).map(c => c.address.slice(0, 8));
  
  // Track last logged count to prevent spam during re-renders
  const lastLoggedUnseenCount = useRef<number>(-1);

  // Only log badge count when it CHANGES and is non-zero (prevent render spam)
  if (unseenCount > 0 && unseenCount !== lastLoggedUnseenCount.current) {
    lastLoggedUnseenCount.current = unseenCount;
    console.log('[CONTACTS_BADGE] Unseen contacts detected', {
      unseenCount,
      unseenAddresses
    });
  } else if (unseenCount === 0 && lastLoggedUnseenCount.current > 0) {
    lastLoggedUnseenCount.current = 0;
  }

  // =====================================================================
  // UI REACTIVITY FIX - Event listeners for contact changes
  // =====================================================================
  // Listen for custom events dispatched by add-contact-dialog.tsx and provider.tsx
  useEffect(() => {
    const handleContactAdded = (event: CustomEvent) => {
      console.debug("[CONTACTS-REFRESH] Received moby-contact-added event", {
        contactAddress: event.detail?.contactAddress?.slice(0, 16) + "...",
        contactName: event.detail?.contactName,
        timestamp: event.detail?.timestamp,
        source: event.detail?.source || 'local',
        currentRefreshTrigger: deletionRefreshTrigger
      });
      
      // Increment refresh trigger to force useLiveQuery to re-execute
      setDeletionRefreshTrigger(prev => {
        const newValue = prev + 1;
        console.debug("[CONTACTS-REFRESH] Incrementing refresh trigger", { from: prev, to: newValue });
        return newValue;
      });
    };
    
    const handleConversationDeleted = (event: CustomEvent) => {
      console.debug("[CONTACTS-REFRESH] Received moby-conversation-deleted event", {
        conversationAddress: event.detail?.conversationAddress?.slice(0, 16) + "...",
        deletionCounter: event.detail?.deletionCounter
      });
      
      // Increment refresh trigger to force useLiveQuery to re-execute
      setDeletionRefreshTrigger(prev => prev + 1);
    };
    
    const handleContactDeleted = (event: CustomEvent) => {
      console.debug("[CONTACTS-REFRESH] Received moby-contact-deleted event", {
        contactAddress: event.detail?.contactAddress?.slice(0, 16) + "...",
        deletionCounter: event.detail?.deletionCounter
      });
      
      // Increment refresh trigger to force useLiveQuery to re-execute
      setDeletionRefreshTrigger(prev => prev + 1);
    };
    
    // Add event listeners
    window.addEventListener('moby-contact-added', handleContactAdded as EventListener);
    window.addEventListener('moby-conversation-deleted', handleConversationDeleted as EventListener);
    window.addEventListener('moby-contact-deleted', handleContactDeleted as EventListener);
    
    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('moby-contact-added', handleContactAdded as EventListener);
      window.removeEventListener('moby-conversation-deleted', handleConversationDeleted as EventListener);
      window.removeEventListener('moby-contact-deleted', handleContactDeleted as EventListener);
    };
  }, []);

  // Clear unseen status when drawer opens
  useEffect(() => {
    if (isOpen && unseenCount > 0) {
      // Mark all unseen contacts as seen
      const markContactsSeen = async () => {
        // Filter contacts with unseen status and update them
        const unseenContacts = await db.contacts.filter(contact => contact.custom?.unseen === true).toArray();
        for (const contact of unseenContacts) {
          await db.contacts.update(contact.address, {
            custom: {
              ...contact.custom,
              unseen: false,
            },
          });
        }
      };
      markContactsSeen();
    }
  }, [isOpen, unseenCount, db]);

  // Contact sharing logic
  if (!session) {
    throw new Error("Session must be defined.");
  }
  
  const contactDetails = createAndSignContactDetails(session);
  const shareCode = bytesToHex(contactDetails);
  const shareUrl = `${window.location.origin}/chats/#${shareCode}`;
  
  const shareContactFormSchema = z.object({
    "their-code": z
      .string()
      .trim()
      .regex(/^[0-9a-f]+$/, "Codes must be hex-encoded.")
      .min(64, "Codes must be at least 64 characters long.")
      .max(256, "Codes must be less than 256 characters long."),
  });
  
  const handleAddContact = async (e: FormEvent) => {
    e.preventDefault();
    
    const form = shareContactFormSchema.safeParse({
      "their-code": theirCode,
    });
    
    if (!form.success) {
      for (const error of form.error.errors) {
        toast.error(error.message);
      }
      return;
    }
    
    const code = form.data["their-code"];
    if (code === shareCode) {
      toast.error("You cannot add yourself as a contact.");
      return;
    }
    
    try {
      parseAndVerifyContactDetails(code);
    } catch (err) {
      if (!(err instanceof Error)) {
        throw err;
      }
      toast.error(err.message);
      return;
    }
    
    navigate(`${window.location.pathname}#${code}`, { replace: true });
    setIsOpen(false); // Close drawer when AddContactDialog opens for smoother UX transition
    setTheirCode('');
  };
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(shareCode);
    toast.success("Copied to clipboard");
  };

  const handleSendInvite = async () => {
    const gatewayUrl = getEmailGatewayUrl();
    const account = emailAccounts.find(a => a.id === selectedFromAccount);
    
    if (!gatewayUrl || !account) {
      toast.error(!gatewayUrl ? "Email gateway not configured" : "No email account selected");
      return;
    }
    
    if (!inviteEmail.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    
    // Split comma/semicolon-separated emails into individual addresses
    const emailAddresses = inviteEmail
      .split(/[,;]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    if (emailAddresses.length === 0) {
      toast.error("At least one valid email is required");
      return;
    }
    
    setInviteSending(true);
    
    try {
      // Build credentials based on auth type
      const credentials: { user: string; pass?: string; accessToken?: string } = {
        user: account.username,
      };
      if (account.authType === "oauth2" && account.accessToken) {
        credentials.accessToken = account.accessToken;
      } else {
        credentials.pass = account.password;
      }
      
      // Build HTML email body with invite link and fallback key string
      // Uses window.location.origin to ensure correct server URL (test vs production)
      const inviteCode = '9MOBYCHAT2025INVITE999'; // Onboarding invite code for new users
      const inviteLink = `${window.location.origin}/chats/?invite=${inviteCode}#${shareCode}`;
      const personalNote = inviteMessage.trim() ? `<p>${inviteMessage.trim()}</p>` : '';
      
      const emailBody = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
${personalNote}
<p>You've been invited to connect on MobyChat!</p>

<p><strong>New to MobyChat?</strong><br>
Use this Invite Code during signup: <strong style="color: #0066cc;">${inviteCode}</strong></p>

<p><a href="${inviteLink}" style="display: inline-block; padding: 12px 24px; background-color: #0066cc; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">Click Here to Connect</a></p>

<p style="margin-top: 24px; color: #666;">Or manually paste this code in MobyChat:</p>
<p style="font-family: monospace; background-color: #f5f5f5; padding: 12px; border-radius: 4px; word-break: break-all;">
${shareCode}
</p>

<hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
<p style="color: #888; font-size: 12px;">MobyChat - Secure, decentralized messaging</p>
</body>
</html>`;
      
      // Send individual email + create individual pending entry for each address
      let successCount = 0;
      let failCount = 0;
      
      for (const recipientEmail of emailAddresses) {
        try {
          const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              host: account.smtpHost,
              port: account.smtpPort,
              secure: account.smtpPort === 465,
              credentials,
              from: account.email,
              to: recipientEmail,
              subject: `${session?.name || 'Someone'} invited you to MobyChat`,
              body: emailBody,
              html: true,
            }),
          });
          
          const data = await response.json().catch(() => ({ success: false, error: "Invalid response" }));
          
          if (response.ok && data.success) {
            // Store individual pending invite in local database
            const inviteId = crypto.randomUUID();
            const now = new Date();
            const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
            
            await db.pendingInvites.add({
              id: inviteId,
              recipientEmail,
              senderAddress: session?.address || '',
              senderKeyString: shareCode,
              status: 'pending',
              createdAt: now,
              expiresAt,
              personalMessage: inviteMessage.trim() || undefined,
              syncedToServer: 0,
            });
            
            console.log('[INVITE] Stored pending invite', { inviteId, recipientEmail });
            successCount++;
          } else {
            console.warn('[INVITE] Failed for', recipientEmail, data.error);
            failCount++;
          }
        } catch (err) {
          console.error('[INVITE] Send failed for', recipientEmail, err);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(successCount === 1 
          ? `Invite sent to ${emailAddresses[0]}`
          : `${successCount} invite${successCount > 1 ? 's' : ''} sent successfully`
        );
        setInviteEmail('');
        setInviteMessage('');
        setDeletionRefreshTrigger(prev => prev + 1);
      }
      if (failCount > 0) {
        toast.error(`${failCount} invite${failCount > 1 ? 's' : ''} failed to send`);
      }
    } catch (error) {
      console.error("[INVITE] Send failed:", error);
      toast.error("Failed to send invites");
    } finally {
      setInviteSending(false);
    }
  };

  return (
    <>
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {typeof children === 'function' ? children({ unseenCount }) : children}
        </mui.Drawer.Trigger>
      ) : (
        typeof children === 'function' ? children({ unseenCount }) : children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-80 overflow-hidden">
          {/* Pointer arrow — visible on desktop */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <h2 className="text-headline-sm">MobyContacts™</h2>
            <div className="flex items-center gap-2">
              <div className="text-body-sm text-on-surface-variant">
                {(contacts ?? []).length} contact{(contacts ?? []).length !== 1 ? 's' : ''}
              </div>
              <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8" aria-label="Close drawer">
                <XIcon size={20} />
              </button>
            </div>
          </div>
          
          {/* Add Contact Section - Collapsible */}
          <div className="border-b border-outline-variant">
            {/* Header - Always visible */}
            <button
              onClick={() => setIsAddContactExpanded(!isAddContactExpanded)}
              className="w-full p-4 flex items-center justify-between hover:bg-on-surface-variant/5 transition"
            >
              <div className="flex items-center gap-2">
                <PlusIcon size={20} className="text-primary" />
                <h3 className="text-title-md">Add Contact</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowQRCode(true); }}
                  className="p-2 rounded-full hover:bg-on-surface-variant/8 active:bg-on-surface-variant/10 transition"
                  title="Show QR Code"
                >
                  <QrCodeIcon size={18} className="text-on-surface-variant" />
                </button>
                {isAddContactExpanded ? (
                  <ChevronUpIcon size={20} className="text-on-surface-variant" />
                ) : (
                  <ChevronDownIcon size={20} className="text-on-surface-variant" />
                )}
              </div>
            </button>
            
            {/* Collapsible Content */}
            {isAddContactExpanded && (
              <div className="px-4 pb-4 space-y-4">
                {/* Email Invite Section - Primary */}
                <div className="space-y-3 p-3 rounded-lg bg-surface-container border border-outline-variant">
                  <div className="flex items-center gap-2 text-label-md text-on-surface">
                    <MailIcon size={16} className="text-primary" />
                    <span>Invite via Email</span>
                  </div>
                  
                  <div className="space-y-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="recipient@email.com"
                      className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                    />
                    <textarea
                      value={inviteMessage}
                      onChange={(e) => setInviteMessage(e.target.value)}
                      placeholder="Personal message (optional)"
                      rows={2}
                      className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary resize-none"
                    />
                  </div>
                  
                  {/* From: Email Account Selection */}
                  <div className="flex items-center gap-2">
                    <span className="text-label-sm text-on-surface-variant whitespace-nowrap">From:</span>
                    {emailAccounts.length > 0 ? (
                      <select
                        value={selectedFromAccount}
                        onChange={(e) => setSelectedFromAccount(e.target.value)}
                        aria-label="Select sender email account"
                        className="flex-1 px-2 py-1.5 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface focus:outline-none focus:border-primary"
                      >
                        {emailAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex-1 flex items-center gap-2 text-body-sm text-on-surface-variant">
                        <AlertCircle size={14} className="text-warning" />
                        <span>Add email account in Settings</span>
                      </div>
                    )}
                  </div>
                  
                  <button
                    disabled={!inviteEmail.trim() || inviteSending || emailAccounts.length === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-lg text-body-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    onClick={handleSendInvite}
                  >
                    {inviteSending ? "Sending..." : "Send Invite"}
                    <SendIcon size={14} />
                  </button>
                </div>
                
                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-outline-variant" />
                  <span className="text-label-sm text-on-surface-variant">Or</span>
                  <div className="flex-1 border-t border-outline-variant" />
                </div>
                
                {/* Legacy Key String Section - Secondary */}
                <div className="space-y-3">
                  {/* Your Code */}
                  <div
                    className="group relative hover:cursor-pointer rounded-lg border border-outline-variant p-3 bg-surface-container"
                    onClick={handleCopyCode}
                    title="Click to copy your code"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-label-sm text-on-surface-variant">Your code</span>
                      <CopyIcon size={14} className="text-on-surface-variant" />
                    </div>
                    <code className="text-body-sm font-mono text-on-surface truncate block select-all">
                      {shareCode}
                    </code>
                    <span
                      aria-hidden
                      className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-lg transition"
                    />
                  </div>
                  
                  {/* Their Code */}
                  <form onSubmit={handleAddContact} className="space-y-2">
                    <div>
                      <label className="text-label-sm text-on-surface-variant block mb-1">Their code</label>
                      <input
                        type="text"
                        value={theirCode}
                        onChange={(e) => setTheirCode(e.target.value)}
                        placeholder="Paste contact code here"
                        className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm font-mono text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                      />
                    </div>
                    <mui.Button
                      type="submit"
                      variant="secondary"
                      disabled={!theirCode.trim()}
                      className="w-full"
                    >
                      Add via Code
                    </mui.Button>
                  </form>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {(contacts ?? []).length === 0 && (pendingInvites ?? []).length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center text-on-surface-variant">
                  <div className="text-body-lg mb-2">No contacts yet</div>
                  <div className="text-body-sm">Add contacts to start chatting</div>
                </div>
              </div>
            ) : (
              <div className="divide-y-outline divide-y">
                {/* Pending Invites Section - Collapsible */}
                {(pendingInvites ?? []).length > 0 && (
                  <>
                    <button
                      onClick={() => setIsPendingExpanded(!isPendingExpanded)}
                      className="w-full px-4 py-2 bg-surface-container-low flex items-center justify-between hover:bg-on-surface-variant/5 transition"
                    >
                      <span className="text-label-sm text-on-surface-variant">
                        Pending Invites ({(pendingInvites ?? []).length})
                      </span>
                      {isPendingExpanded ? (
                        <ChevronUpIcon size={14} className="text-on-surface-variant" />
                      ) : (
                        <ChevronDownIcon size={14} className="text-on-surface-variant" />
                      )}
                    </button>
                    {isPendingExpanded && (pendingInvites ?? []).map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center gap-3 p-4 hover:bg-on-surface-variant/5"
                      >
                        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-container-high border border-dashed border-outline-variant">
                          <MailIcon size={18} className="text-on-surface-variant" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-body-md text-on-surface truncate">
                            {invite.recipientEmail}
                          </div>
                          <div className="flex items-center gap-2 text-label-sm text-on-surface-variant flex-wrap">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-warning/10 text-warning">
                              <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse" />
                              Pending
                            </span>
                            {invite.groupName && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
                                {invite.groupName}
                              </span>
                            )}
                            <span>·</span>
                            <span>{new Date(invite.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Contacts Section */}
                {(contacts ?? []).length > 0 && (pendingInvites ?? []).length > 0 && (
                  <div className="px-4 py-2 bg-surface-container-low">
                    <span className="text-label-sm text-on-surface-variant">
                      Contacts ({(contacts ?? []).length})
                    </span>
                  </div>
                )}
                {(contacts ?? []).map((contact) => (
                  <ContactListItem
                    key={contact.address}
                    contact={contact}
                    onEditClick={() => setEditingContact(contact)}
                  />
                ))}
              </div>
            )}
          </div>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>
    
    {/* QR Code Modal */}
    <mui.Dialog.Root open={showQRCode} onOpenChange={setShowQRCode}>
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop onClick={(e) => e.stopPropagation()} />
        <mui.Dialog.Popup className="bg-surface-container-high text-on-surface fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
          <mui.Dialog.Title className="text-title-lg mb-4">
            Share your contact information
          </mui.Dialog.Title>
          
          <div className="space-y-4">
            <div className="grid place-items-center">
              <QRCodeSVG
                value={shareUrl}
                size={256}
                className="rounded-lg"
                bgColor="#f4fbfa"
                fgColor="#161d1d"
              />
            </div>
            
            <div className="text-center">
              <p className="text-body-sm text-on-surface-variant mb-2">
                Scan this QR code to add contact
              </p>
              <code className="text-label-sm text-on-surface-variant bg-surface-container px-3 py-1 rounded-md select-all break-all">
                {shareUrl}
              </code>
            </div>
            
            <mui.Button
              onClick={() => setShowQRCode(false)}
              variant="primary"
              filled
              className="w-full"
            >
              Close
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
    
    {/* Edit Contact Dialog - Rendered outside drawer so it persists when drawer closes */}
    {editingContact && (
      <EditContactDialog 
        contact={editingContact}
        open={editingContact !== null}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setEditingContact(null);
          }
        }}
      >
        <div className="hidden" />
      </EditContactDialog>
    )}
    </>
  );
}

function ContactListItem({ contact, onEditClick }: { contact: any; onEditClick?: () => void }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [location, navigate] = useLocation();
  const db = useMobyDB();
  const node = useMobyNode();
  const session = useSession();

  const handleDeleteContact = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const userAddress = session?.address || 'unknown';
      
      // FIX #1: Mark peer as deleted to stop infinite retry attempts
      node.markPeerAsDeleted(contact.address);
      
      // CRITICAL FIX: Provide deletedBy parameter for proper tombstone creation
      // For local deletion, deletedBy should be the same as userAddress
      await db.deleteContact(contact.address, userAddress, userAddress);
      console.debug(`Contact ${contact.address} deleted successfully`);
      
      // Navigate away from the chat if currently viewing it
      if (location === `/chats/${contact.address}`) {
        navigate('/chats');
      }
      
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete contact:', error);
      // You could add toast notification here
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Link
        to={`/chats/${contact.address}`}
        className="group relative block p-4 hover:cursor-pointer"
        onClick={() => {
          // PANEL FIX 3: Clear overlay panels so ChatPanel renders
          window.dispatchEvent(new CustomEvent("email-panel-close"));
          window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
          window.dispatchEvent(new CustomEvent("calendar-panel-close"));
        }}
      >
        <span
          aria-hidden
          className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 transition"
        />

        <div className="flex gap-4">
          <div className="relative size-12 shrink-0">
            <div 
              className="grid size-12 place-items-center rounded-full text-white"
              style={{ backgroundColor: getPeerAvatarColor(contact.custom?.profile) }}
            >
              <div className="text-title-sm">{contact.getInitials()}</div>
              {renderPeerAvatarEmoji(contact.custom?.profile, "md")}
            </div>
            
            {/* Presence badge from remote peer profile */}
            {renderPeerPresenceBadge(contact.custom?.profile, "md")}
            
            {/* Connection health badge — P0-C: presence-aware derived state */}
            <PeerHealthDot
              peerId={contact.address}
              connection={contact.connectionHealth}
              size="md"
            />

          </div>
          <div className="min-w-0 flex-1 self-center">
            <div className="text-title-md mb-1 truncate">
              {contact.getDisplayName()}
            </div>
            <div className="text-body-sm text-on-surface-variant truncate font-mono">
              {contact.address}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeerDiagnosticBadge peerId={contact.address} />
            <button
              className="group/chat-btn relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-primary"
              title="Start chat"
            >
              <span
                aria-hidden
                className="group-hover/chat-btn:bg-on-surface-variant/8 group-active/chat-btn:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
              />
              <MessageSquareIcon size={20} />
            </button>
            
            {/* Edit contact button */}
            <button
              className="group/edit-btn relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-on-surface-variant opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit contact nickname"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditClick?.();
              }}
            >
              <span
                aria-hidden
                className="group-hover/edit-btn:bg-on-surface-variant/8 group-active/edit-btn:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
              />
              <PencilIcon size={16} />
            </button>
            
            {/* Delete contact button */}
            <button
              onClick={handleDeleteContact}
              className="group/delete-btn relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-error opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete contact"
            >
              <span
                aria-hidden
                className="group-hover/delete-btn:bg-error/8 group-active/delete-btn:bg-error/10 absolute inset-0 rounded-full transition"
              />
              <Trash2Icon size={16} />
            </button>
          </div>
        </div>
      </Link>

      {/* Delete confirmation dialog */}
      <mui.Dialog.Root open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <mui.Dialog.Portal>
          <mui.Dialog.Backdrop onClick={(e) => e.stopPropagation()} />
          <mui.Dialog.Popup className="bg-surface-container-high text-on-surface fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <mui.Dialog.Title className="text-title-lg mb-4">
              Delete Contact
            </mui.Dialog.Title>
            <mui.Dialog.Description className="text-body-md text-on-surface-variant mb-6">
              Are you sure you want to permanently delete <strong>{contact.getDisplayName()}</strong>? 
              This will remove the contact and all associated chat history. This action cannot be undone.
            </mui.Dialog.Description>
            <div className="flex justify-end gap-2">
              <mui.Button 
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="text-primary"
              >
                Cancel
              </mui.Button>
              <mui.Button 
                onClick={confirmDelete}
                disabled={isDeleting}
                filled
                className="bg-error text-on-error hover:bg-error/90"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </mui.Button>
            </div>
          </mui.Dialog.Popup>
        </mui.Dialog.Portal>
      </mui.Dialog.Root>
    </>
  );
}
